import { Component, inject, OnInit, signal, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { EmployeeService } from '../services/employee.service';
import { AuthService } from '../services/auth.service';
import { EmployeeListItem } from '../shared/models/employee.model';
import { SlSearchComponent } from '../shared/wrapper-components/sl-search/sl-search.component';
import { SlButtonComponent } from '../shared/wrapper-components/sl-button/sl-button.component';
import { SlAvatarComponent } from '../shared/wrapper-components/sl-avatar/sl-avatar.component';
import { SlDropdownComponent, DropdownItem } from '../shared/wrapper-components/sl-dropdown/sl-dropdown.component';

@Component({
  selector: 'app-employee-list',
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    NgClass,
    SlSearchComponent,
    SlButtonComponent,
    SlAvatarComponent,
    SlDropdownComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './employee-list.component.html',
  styleUrls: ['./employee-list.component.css']
})
export class EmployeeListComponent implements OnInit {
  private readonly employeeService = inject(EmployeeService);
  public readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  // Local signals
  searchTerm = signal<string>('');

  // Computed properties from service
  employees = this.employeeService.employeeListItems;
  loading = this.employeeService.loading;
  error = this.employeeService.error;
  searchParams = this.employeeService.searchParams;
  hasCreatePermission = this.authService.hasPermission('CREATE_EMPLOYEES');

  // Dynamic action items based on user permissions
  // getActionItems(employee: EmployeeListItem): DropdownItem[] {
  //   const actions: DropdownItem[] = [];

  //   // View Profile - available if user can view all employees or it's their own profile
  //   if (this.authService.hasPermission('VIEW_ALL_EMPLOYEES') ||
  //       (this.authService.hasPermission('VIEW_OWN_PROFILE') && this.isOwnProfile(employee))) {
  //     actions.push({ id: 'view-profile', label: 'View Profile', icon: 'person' });
  //   }

  //   // Timesheet actions - based on permissions
  //   if (this.authService.hasAnyPermission(['VIEW_ALL_TIMESHEETS', 'VIEW_OWN_TIMESHEET'])) {
  //     actions.push({ id: 'add-timesheet', label: 'Add Timesheet', icon: 'clock' });
  //   }

  //   // Leave requests - based on permissions
  //   if (this.authService.hasAnyPermission(['VIEW_ALL_LEAVE', 'VIEW_OWN_LEAVE'])) {
  //     actions.push({ id: 'leave-requests', label: 'Leave Requests', icon: 'calendar-x' });
  //   }

  //   // Performance and Documents - HR Admin and Manager only
  //   if (this.authService.hasAnyRole(['HR_ADMIN', 'MANAGER'])) {
  //     actions.push({ id: 'performance', label: 'Performance', icon: 'graph-up' });
  //     actions.push({ id: 'documents', label: 'Documents', icon: 'file-text' });
  //   }

  //   // Add divider if we have actions and more actions to come
  //   if (actions.length > 0 && (this.authService.hasPermission('EDIT_ALL_EMPLOYEES') ||
  //       this.authService.hasPermission('EDIT_OWN_PROFILE') || this.authService.hasRole('HR_ADMIN'))) {
  //     actions.push({ id: 'divider', label: '', divider: true });
  //   }

  //   // Edit Employee - available if user can edit all employees or it's their own profile
  //   if (this.authService.hasPermission('EDIT_ALL_EMPLOYEES') ||
  //       (this.authService.hasPermission('EDIT_OWN_PROFILE') && this.isOwnProfile(employee))) {
  //     actions.push({ id: 'edit', label: 'Edit Employee', icon: 'pencil' });
  //   }

  //   // Deactivate - HR Admin only
  //   if (this.authService.hasRole('HR_ADMIN')) {
  //     actions.push({ id: 'deactivate', label: 'Deactivate', icon: 'person-x', disabled: false });
  //   }

  //   return actions;
  // }

  // Action items for dropdown
  actionItems: DropdownItem[] = [
    { id: 'view-profile', label: 'View Profile', icon: 'person' },
    { id: 'add-timesheet', label: 'Add Timesheet', icon: 'clock' },
    { id: 'leave-requests', label: 'Leave Requests', icon: 'calendar-x' },
    { id: 'performance', label: 'Performance', icon: 'graph-up' },
    { id: 'documents', label: 'Documents', icon: 'file-text' },
    { id: 'divider', label: '', divider: true },
    { id: 'edit', label: 'Edit Employee', icon: 'pencil' },
    { id: 'deactivate', label: 'Deactivate', icon: 'person-x', disabled: false }
  ];


  private isOwnProfile(employee: EmployeeListItem): boolean {
    const currentUser = this.authService.user();
    return currentUser?.employeeId === employee.id;
  }

  ngOnInit(): void {
    this.loadEmployees();
  }

  loadEmployees(): void {
    this.employeeService.loadEmployees();
  }

  onSearch(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
    this.employeeService.updateSearchParams({ searchTerm });
  }

  onClearSearch(): void {
    this.searchTerm.set('');
    this.employeeService.clearSearch();
  }

  onSortByName(): void {
    const currentParams = this.searchParams();
    if (currentParams.sortBy === 'name') {
      this.employeeService.toggleSortOrder();
    } else {
      this.employeeService.setSortBy('name');
    }
  }

  onActionSelected(employee: EmployeeListItem, action: DropdownItem): void {
    console.log('Action selected:', action.id, 'for employee:', employee.fullName);

    switch (action.id) {
      case 'view-profile':
        this.viewProfile(employee);
        break;
      case 'add-timesheet':
        this.addTimesheet(employee);
        break;
      case 'leave-requests':
        this.manageLeaveRequests(employee);
        break;
      case 'performance':
        this.viewPerformance(employee);
        break;
      case 'documents':
        this.manageDocuments(employee);
        break;
      case 'edit':
        this.editEmployee(employee);
        break;
      case 'deactivate':
        this.deactivateEmployee(employee);
        break;
    }
  }

  viewProfile(employee: EmployeeListItem): void {
    // Navigate to employee profile
    this.router.navigate(['/employees', employee.id, 'profile']);
  }

  addTimesheet(employee: EmployeeListItem): void {
    // Navigate to timesheet creation
    console.log('Adding timesheet for:', employee.fullName);
  }

  manageLeaveRequests(employee: EmployeeListItem): void {
    // Navigate to leave requests
    console.log('Managing leave requests for:', employee.fullName);
  }

  viewPerformance(employee: EmployeeListItem): void {
    // Navigate to performance view
    console.log('Viewing performance for:', employee.fullName);
  }

  manageDocuments(employee: EmployeeListItem): void {
    // Navigate to document management
    console.log('Managing documents for:', employee.fullName);
  }

  editEmployee(employee: EmployeeListItem): void {
    // Navigate to employee edit form
    console.log('Editing employee:', employee.fullName);
  }

  deactivateEmployee(employee: EmployeeListItem): void {
    // Show confirmation dialog and deactivate
    console.log('Deactivating employee:', employee.fullName);
  }

  getEmployeeInitials(fullName: string): string {
    return fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  getSortIcon(): string {
    const params = this.searchParams();
    if (params.sortBy === 'name') {
      return params.sortOrder === 'asc' ? 'sort-alpha-down' : 'sort-alpha-up';
    }
    return 'sort-alpha-down';
  }

  trackByEmployeeId(_index: number, employee: EmployeeListItem): string {
    return employee.id;
  }

  addEmployee(): void {
    this.router.navigate(['/employees/add']);
  }
}
